import type { ActionButtonProps } from '@openx/components-header/HeaderActions/ActionButton.tsx';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { EDITABLE_STATUSES } from '../../AudiencesList/constants.ts';
import { useArchiveAudiences } from '../../AudiencesList/hooks/useArchiveAudiences.tsx';
import { useDuplicateAudience } from '../../AudiencesList/hooks/useDuplicateAudience.tsx';
import { useShareAudience } from '../../AudiencesList/hooks/useShareAudience.tsx';
import { useAudienceDetails } from '../tabs/details/hooks/useAudienceDetails.ts';

export const useHeaderMenu = () => {
  const { t } = useTranslation();
  const { audienceUid = '' } = useParams<{
    audienceUid: string;
  }>();
  const { data } = useAudienceDetails(audienceUid);
  const { openShareModal, isLoading: shareLoading } = useShareAudience();
  const { openArchiveModal, isLoading: archiveLoading } = useArchiveAudiences();
  const { openDuplicateModal, isLoading: duplicateLoading } = useDuplicateAudience();
  const navigate = useNavigate();

  const menuActions: ActionButtonProps[] = useMemo(() => {
    if (!data) {
      return [];
    }

    return [
      {
        label: t('Duplicate'),
        color: 'secondary',
        isAllowed: true,
        loading: duplicateLoading,
        disabled: duplicateLoading,
        'data-test': 'audience-details-duplicate-button',
        onClick: () => {
          openDuplicateModal({
            audience: {
              id: data.id,
              name: data.name,
              account: data.account ? { name: data.account.name } : null,
            },
          });
        },
      },
      {
        label: t('Create Deal'),
        color: 'secondary',
        isAllowed: true,
        'data-test': 'audience-details-create-deal-button',
        onClick: () => {
          navigate('/deals/create', {
            state: {
              audienceId: data.id,
              accountUid: data.account?.uid,
              useAsTemplate: true,
            },
          });
        },
      },
      {
        label: t('Use as template'),
        color: 'secondary',
        isAllowed: true,
        'data-test': 'audience-details-use-as-template-button',
        onClick: () => {
          navigate('/audiences/create', {
            state: {
              audienceId: data.id,
              useAsTemplate: true,
            },
          });
        },
      },
    ];
  }, [t, data, duplicateLoading, openDuplicateModal, navigate]);

  const actions: ActionButtonProps[] = useMemo(() => {
    if (!data) {
      return [];
    }

    const canEditAudience = (EDITABLE_STATUSES as readonly string[]).includes(data.status);

    return [
      {
        label: t('Share Audience'),
        color: 'primary',
        showAsActionButton: true,
        'data-test': 'audience-details-share-audience-button',
        loading: shareLoading,
        disabled: shareLoading,
        onClick: () => {
          openShareModal({
            audience: {
              id: data.id,
              name: data.name,
            },
          });
        },
      },
      {
        label: t('Edit'),
        color: 'secondary',
        'data-test': 'audience-details-edit-button',
        isAllowed: canEditAudience,
        notAllowedMessage: t('This audience cannot be edited in its current status.'),
        onClick: () => {
          navigate(`/audiences/${audienceUid}/edit`);
        },
        showAsActionButton: true,
      },
      {
        label: t('Archive'),
        color: 'error',
        showAsActionButton: true,
        'data-test': 'audience-details-archive-button',
        loading: archiveLoading,
        disabled: archiveLoading,
        onClick: () => {
          openArchiveModal({
            audiences: [{ id: data.id, name: data.name }],
            onSuccess: () => {
              navigate('/audiences');
            },
          });
        },
      },
      ...menuActions,
    ];
  }, [t, menuActions, data, openShareModal, audienceUid, navigate, shareLoading, archiveLoading, openArchiveModal]);

  return useMemo(() => actions, [actions]);
};
